import { KeyboardBackspace } from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetSubscriptionByIdQuery } from "src/api/services/patientSubscriptionApi";
import { MatchParam } from "src/constants/matches";
import { Paths } from "src/constants/paths";
import { useIsMobile } from "src/hooks/useIsMobile";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import PlanCard from "../components/PlanCard";
import ShippingCard from "../components/ShippingCard";
import InjectionGuideCard from "src/pages/PatientDashboard/Care/components/InjectionGuideCard";
import MedInstructionsCard from "../components/MedInstructionsCard";
import ChatCard from "../components/ChatCard";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import { enUS } from "date-fns/locale";

const SubscriptionDetails = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { subscriptionId } = useParams<MatchParam<"SUBSCRIPTION_ID">>();
  const { t } = useTranslation();
  const page = "subscription-details";
  const locale = usePatientLocale();

  const { data } = useGetSubscriptionByIdQuery(
    subscriptionId ? Number(subscriptionId) : skipToken,
  );

  const subscription = useMemo(() => {
    if (data == null) return;

    return data;
  }, [data]);

  const isGlp1 = useMemo(
    () => subscription?.product.is_glp1,
    [subscription?.product.is_glp1],
  );

  const canceled = useMemo(() => {
    if (subscription == null) return;

    return (
      subscription.status === "canceled" ||
      subscription.status === "scheduled_for_cancellation"
    );
  }, [subscription]);

  const renewalDate = useMemo(() => {
    if (canceled) return null;
    if (subscription?.current_period_end == null) return null;

    const dateFormat =
      locale === enUS ? "MMMM do, yyyy" : "'el' EEEE dd 'de' MMMM";

    return format(new Date(subscription.current_period_end), dateFormat, {
      locale,
    });
  }, [canceled, locale, subscription?.current_period_end]);

  const productName = useMemo(() => {
    if (subscription == null) return null;

    if (subscription.product.slug === "fhl") {
      return subscription.order.medication;
    }

    return subscription.product.name;
  }, [subscription]);

  return (
    <Stack gap={2} width="100%">
      <Stack
        direction={!isMobile ? "column" : "row"}
        alignItems={!isMobile ? "" : "center"}
        gap={1}
      >
        <Stack direction="row" alignItems="center">
          <IconButton sx={{ color: "#00B6B8" }} onClick={() => navigate(-1)}>
            <KeyboardBackspace fontSize={!isMobile ? "small" : "large"} />
          </IconButton>
          {!isMobile ? (
            <Typography color="#00B6B8">{t(`back`, "Back")}</Typography>
          ) : null}
        </Stack>
        <Typography variant="h5" fontWeight={700}>
          {t(`${page}.header`, "Subscription Details")}
        </Typography>
      </Stack>
      {isMobile ? (
        <Stack gap={1}>
          <Typography variant="h5" fontWeight={700}>
            {t(
              `medications.${subscription?.product.display_name?.toLowerCase()}.capitalized`,
              subscription?.product.name || "",
            )}
          </Typography>
          {renewalDate ? (
            <Typography variant="subtitle1">
              {t(`${page}.nextRefill`, "Next refill")} {renewalDate}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      <Stack alignItems="center" gap={3}>
        <MedicationIcon
          name={productName || ""}
          height={180}
          width={150}
          slug={subscription?.product.slug ?? ""}
        />
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate(`${Paths.CARE}/${subscription?.product_id}`)}
        >
          {t(`${page}.buttonText`, "Treatment Information")}
        </Button>
      </Stack>
      {subscription ? <PlanCard subscription={subscription} /> : null}
      <ShippingCard />
      {isGlp1 ? <InjectionGuideCard /> : null}
      {subscription ? (
        <MedInstructionsCard subscription={subscription} />
      ) : null}
      <ChatCard productId={subscription?.product_id} />
    </Stack>
  );
};

export default SubscriptionDetails;
